import type { CodigoGrupo } from '@precificador/shared/pricing.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Calculator } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ErroApi } from '../api/cliente';
import { rodadas as apiRodadas, tabelas as apiTabelas } from '../api/endpoints';
import { Aviso } from '../components/Aviso';
import { Botao } from '../components/Botao';
import { CaixaMarcar, Campo } from '../components/Campo';
import { FormParametros, type TextosParametros, lerTextos, paraTextos } from '../components/FormParametros';
import { Carregando, Pagina } from '../components/Pagina';
import { SeletorTabela } from '../components/SeletorTabela';
import { formatarDataHora, lerNumero, paraCampo, plural } from '../lib/formatos';

function lerValor(texto: string): number | null | 'invalido' {
  const n = lerNumero(texto);
  if (n === null) return null;
  return Number.isNaN(n) || n < 0 ? 'invalido' : n;
}

export function NovaRodada() {
  const [params, setParams] = useSearchParams();
  const tabela = params.get('tabela') ?? '';
  const navegar = useNavigate();
  const [textos, setTextos] = useState<TextosParametros>({});
  const [pedidoMinimo, setPedidoMinimo] = useState('');
  const [valorMinParcela, setValorMinParcela] = useState('');
  const [travarPreco, setTravarPreco] = useState(true);
  const [mostrarErros, setMostrarErros] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const prep = useQuery({
    queryKey: ['tabelas', tabela, 'preparacao'],
    queryFn: () => apiTabelas.preparacao(tabela),
    enabled: Boolean(tabela),
    staleTime: 0,
  });

  // Pré-preenche com os últimos valores usados nesta tabela (ou vazio na primeira vez).
  useEffect(() => {
    if (!prep.data) return;
    setTextos(paraTextos(prep.data.grupos.map((g) => g.grupo), prep.data.parametros));
    setPedidoMinimo(paraCampo(prep.data.condicoes.pedidoMinimo, 2));
    setValorMinParcela(paraCampo(prep.data.condicoes.valorMinParcela, 2));
    setTravarPreco(prep.data.travarPreco);
    setMostrarErros(false);
    setErro(null);
  }, [prep.data]);

  const criar = useMutation({
    mutationFn: apiRodadas.criar,
    onSuccess: (r) => navegar(`/rodadas/${r.rodada.id}`),
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível calcular a prévia.'),
  });

  function calcular() {
    setMostrarErros(true);
    setErro(null);
    const { parametros, erros } = lerTextos(textos);
    const primeiro = Object.entries(erros)[0];
    const pm = lerValor(pedidoMinimo);
    const vp = lerValor(valorMinParcela);
    if (primeiro) {
      setErro(`Confira os parâmetros de ${prep.data?.grupos.find((g) => g.grupo === primeiro[0])?.nome}: ${primeiro[1]}`);
      return;
    }
    if (pm === 'invalido' || vp === 'invalido') {
      setErro('Confira o pedido mínimo e o valor mínimo da parcela.');
      return;
    }
    criar.mutate({ tbPreco: tabela, parametros, condicoes: { pedidoMinimo: pm, valorMinParcela: vp }, travarPreco });
  }

  const grupos = (prep.data?.grupos ?? []).map((g) => g.grupo as CodigoGrupo);
  const info = Object.fromEntries(
    (prep.data?.grupos ?? []).map((g) => [
      g.grupo,
      <>
        {plural(g.itens, 'item', 'itens')} na tabela
        {g.semCusto > 0 && <span className="text-aviso"> · {g.semCusto} sem custo</span>}
        {g.semPeso > 0 && <span className="text-aviso"> · {g.semPeso} sem peso</span>}
        {g.repetidos > 0 && <span> · {g.repetidos} com linhas repetidas</span>}
      </>,
    ]),
  );

  return (
    <Pagina titulo="Nova rodada de preços" contexto="Escolha a tabela, confira os parâmetros de cada grupo e calcule a prévia. Nada é gravado no CIGAM nesta etapa.">
      <div className="flex max-w-4xl flex-col gap-5">
        <SeletorTabela
          valor={tabela}
          className="max-w-xl"
          aoMudar={(codigo) => {
            setParams(codigo ? { tabela: codigo } : {}, { replace: true });
            setTextos({});
          }}
        />

        {tabela && prep.isPending && <Carregando texto="Lendo os itens da tabela no CIGAM…" />}
        {prep.error && <Aviso tipo="erro">{prep.error.message}</Aviso>}

        {prep.data && (
          <>
            {prep.data.rascunhos.length > 0 && (
              <Aviso tipo="aviso" titulo="Já existe rascunho desta tabela">
                {prep.data.rascunhos.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ', '}
                    <Link to={`/rodadas/${r.id}`} className="font-medium text-acao underline">
                      rodada {r.id}
                    </Link>{' '}
                    ({formatarDataHora(r.criadoEm)})
                  </span>
                ))}
                . Você pode continuar de onde parou ou criar uma nova.
              </Aviso>
            )}

            {grupos.length === 0 ? (
              <Aviso tipo="info">Esta tabela não tem materiais dos grupos precificados (Rolos e Almofadinhas, Institucional, Biofreezer, Alumínio).</Aviso>
            ) : (
              <>
                <section>
                  <h2 className="mb-2 font-semibold">Parâmetros por grupo</h2>
                  <FormParametros grupos={grupos} textos={textos} aoMudar={setTextos} info={info} mostrarErros={mostrarErros} />
                </section>

                <section>
                  <h2 className="mb-2 font-semibold">Condições da tabela</h2>
                  <div className="grid max-w-xl grid-cols-2 gap-3 rounded-painel border border-linha bg-superficie p-4">
                    <Campo
                      rotulo="Pedido mínimo (R$)"
                      inputMode="decimal"
                      value={pedidoMinimo}
                      onChange={(e) => setPedidoMinimo(e.target.value)}
                      ajuda="Vazio = não aparece no PDF."
                      erro={mostrarErros && lerValor(pedidoMinimo) === 'invalido' ? 'Valor inválido.' : undefined}
                    />
                    <Campo
                      rotulo="Valor mínimo da parcela (R$)"
                      inputMode="decimal"
                      value={valorMinParcela}
                      onChange={(e) => setValorMinParcela(e.target.value)}
                      ajuda="Vazio = não aparece no PDF."
                      erro={mostrarErros && lerValor(valorMinParcela) === 'invalido' ? 'Valor inválido.' : undefined}
                    />
                  </div>
                </section>

                <section>
                  <h2 className="mb-2 font-semibold">Preços atuais</h2>
                  <div className="max-w-xl rounded-painel border border-linha bg-superficie p-4">
                    <CaixaMarcar
                      rotulo="Manter preços atuais"
                      descricao="Nenhum preço muda, nem para mais nem para menos: mantém o preço atual do CIGAM e só recalcula a margem. Na prévia dá para liberar o preço calculado item a item ou digitar o preço."
                      checked={travarPreco}
                      onChange={(e) => setTravarPreco(e.target.checked)}
                    />
                  </div>
                </section>

                {erro && <Aviso tipo="erro">{erro}</Aviso>}
                <div>
                  <Botao variante="principal" carregando={criar.isPending} onClick={calcular}>
                    <Calculator aria-hidden className="size-4" /> Calcular prévia
                  </Botao>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Pagina>
  );
}
